import { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import masterDataService from '../../service/masterData.service';
import documentTypeService from '../../service/newDocumentType.service';
import templateService from '../../service/template.service';
import TemplateFields from './TemplateFields';
import TemplateReports from './TemplateReports';
import { TemplateType } from '../../config/template';

export const TemplatePage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    documentType: [],
    form: [],
    language: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({
    documentType: [],
    form: [],
    language: [],
  });

  const def = {
    name: '',
    templateType: '',
    documentLevel: null,
    documentTypeId: null,
    formId: null,
    languageCode: null,
    subject: '',
    body: '',
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    initDefaultValues();
    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('template');
    setCanEdit(submenuId ? getCanEdit(submenuId) : true);
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues, defaultSelectedValues, defaultValues]);

  const initDefaultValues = () => {
    Promise.all([
      documentTypeService.allDocumentTypes(),
      masterDataService.formTypeSubmenus(),
      masterDataService.languageToForm(),
    ]).then(([documentType, form, language]) => {
      setAutoCompleteData({
        documentType: Array.isArray(documentType) ? documentType : [],
        form: Array.isArray(form) ? form : [],
        language: Array.isArray(language) ? language : [],
      });

      setAutoCompleteOptions({
        documentType: generateAutocompleteItems(documentType ?? [], 'name', 'documentTypeId'),
        form: generateAutocompleteItems(form ?? [], 'subMenuName', 'subMenuId'),
        language: generateAutocompleteItems(language ?? [], ['code', 'name'], 'code'),
      });
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    templateType: Yup.string().required(),
    documentLevel: Yup.string()
      .nullable()
      .when('templateType', {
        is: TemplateType.TEXT,
        then: (schema) => schema.required(),
      }),
    documentTypeId: Yup.number().nullable(),
    formId: Yup.number().nullable(),
    languageCode: Yup.string().nullable(),
    subject: Yup.string().nullable(),
    body: Yup.string().required(),
  });

  const valuesToForm = () => {
    const { name, templateType, documentLevel, documentTypeId, formId, languageCode, subject, body } = selectedValues;

    setInitValues({
      name: name ?? '',
      templateType: templateType ?? '',
      documentLevel: documentLevel ?? null,
      documentTypeId: documentTypeId ?? null,
      formId: formId ?? null,
      languageCode: languageCode ?? null,
      subject: subject ?? '',
      body: body ?? '',
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const normalizeTemplatePayload = (values) => {
    const cleanedValues = cleanValues(values);
    const isWorksheetForm = autoCompleteData.form.some(
      (form) => Number(form.subMenuId) === Number(values.formId) && form.url === 'worksheetReport'
    );

    return {
      ...cleanedValues,
      documentLevel: values.templateType === TemplateType.TEXT ? values.documentLevel : null,
      documentTypeId:
        isWorksheetForm || values.templateType === TemplateType.ELECTRONIC_INVOICE_EMAIL
          ? null
          : cleanedValues.documentTypeId,
      subject: [TemplateType.EMAIL, TemplateType.ELECTRONIC_INVOICE_EMAIL].includes(values.templateType)
        ? cleanedValues.subject ?? null
        : null,
      formId: values.templateType === TemplateType.ELECTRONIC_INVOICE_EMAIL ? null : cleanedValues.formId,
    };
  };

  const updateObject = async (values) => {
    const updData = {
      ...normalizeTemplatePayload(values),
      id: selectedValues.id,
      updatedBy: user.userId,
    };

    templateService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...normalizeTemplatePayload(values),
      createdBy: user.userId,
    };

    templateService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel([data.id]);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };

    templateService.deleteItem(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(def);
      setRowSelectionModel([]);
    });
  };

  return (
    <>
      <FormBase
        name="Template"
        leftPanelContent={
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultSelectedValues);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              deleteClick={() => {
                setShowDeleteDialog(true);
              }}
              deleteDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={() => {
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
            />

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ resetForm }) => (
                <Form>
                  <TemplateFields
                    viewField={viewField}
                    autoCompleteOptions={autoCompleteOptions}
                    forms={autoCompleteData.form}
                  />

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);

                      resetForm();
                    }}
                    disabled={editButtonVisible && addField}
                  />
                </Form>
              )}
            </Formik>
          </div>
        }
        rightPanelContent={
          <TemplateReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            documentTypes={autoCompleteData.documentType}
            forms={autoCompleteData.form}
            languages={autoCompleteData.language}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default TemplatePage;
